// Frontend service for Grading Policy Manager with TanStack Query v5 support
// File: src/services/gradingPolicyService.js

import axios from 'axios';
import { ProgramTypes, buildProgramLabel } from '../models/gradingPolicy';
import { DEFAULT_CONESTOGA_POLICIES } from '../data/defaultConestogaPolicies';
import { computeGrade } from '../utils/computeGrade';

class GradingPolicyService {
    constructor() {
        // Always rely on local JSON data when no API URL is configured so the app works fully offline
        this.useLocalData = !process.env.REACT_APP_API_URL;

        this.storageKey = 'gradingPolicies';
        this.programDefaultsKey = 'programPolicyDefaults';
        this.currentProgramTypeKey = 'currentProgramTypeSelection';
        this.lastViewedPolicyKey = 'lastViewedPolicyId';

        if (!this.useLocalData) {
            this.client = axios.create({
                baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
                timeout: 5000,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        }

        // policies for local development
        this.localPolicies = this.initializeLocalPolicies();
        this.initializeDefaults();
    }

    normalizeProgramType(programType) {
        if (!programType) return null;

        const normalizedInput = programType.toString().trim().toLowerCase();

        const matches = ProgramTypes.find(type => type.toLowerCase() === normalizedInput);
        if (matches) return matches;

        // Handle common shortcuts and legacy values from earlier implementations
        const legacyMap = {
            degree: 'Degree',
            diploma: 'Diploma',
            certificate: 'Certificate',
            advanced: 'Advanced Diploma',
            'advanced diploma': 'Advanced Diploma',
            grad: 'Graduate Certificate',
            'graduate certificate': 'Graduate Certificate',
            apprenticeship: 'Apprenticeship'
        };

        return legacyMap[normalizedInput] || programType;
    }

    normalizePolicyShape(policy) {
        const normalizedScale = (policy.gradeScale || []).map(scale => ({
            alphaGrade: scale.alphaGrade || scale.letter || '',
            gradePoint: typeof scale.gradePoint === 'number' ? scale.gradePoint : (scale.gpa ?? 0),
            lowPercent: typeof scale.lowPercent === 'number' ? scale.lowPercent : (scale.minPercentage ?? 0),
            highPercent: typeof scale.highPercent === 'number' ? scale.highPercent : (scale.maxPercentage ?? 0),
            description: scale.description || (scale.passingGrade === false ? 'Fail' : '')
        }));

        const normalizedPrograms = policy.associatedProgramTypes || policy.programTypes || [];

        return {
            ...policy,
            associatedProgramTypes: normalizedPrograms,
            // Maintain backward compatibility with earlier "programTypes" property
            programTypes: normalizedPrograms,
            gradeScale: normalizedScale,
            isActive: policy.isActive !== undefined ? policy.isActive : true
        };
    }

    getStoredPolicies() {
        const normalizeList = (list) => {
            const normalized = list.map(policy => this.normalizePolicyShape(policy));
            this.localPolicies = normalized.reduce((acc, policy) => {
                acc[policy.id] = policy;
                return acc;
            }, {});
            return normalized;
        };

        if (typeof window === 'undefined' || !window.localStorage) {
            return normalizeList(Object.values(this.localPolicies));
        }
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (stored) return normalizeList(JSON.parse(stored));
        } catch (error) {
            console.error('Failed to read stored grading policies', error);
        }
        return normalizeList(Object.values(this.localPolicies));
    }

    getLastViewedPolicyId() {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        try {
            return localStorage.getItem(this.lastViewedPolicyKey);
        } catch (error) {
            console.error('Failed to read last viewed policy', error);
            return null;
        }
    }

    setLastViewedPolicyId(policyId) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            localStorage.setItem(this.lastViewedPolicyKey, policyId);
        } catch (error) {
            console.error('Failed to persist last viewed policy', error);
        }
    }

    savePolicies(policies) {
        const normalized = policies.map(policy => this.normalizePolicyShape(policy));
        this.localPolicies = normalized.reduce((acc, policy) => {
            acc[policy.id] = policy;
            return acc;
        }, {});

        if (typeof window === 'undefined' || !window.localStorage) return;

        try {
            localStorage.setItem(this.storageKey, JSON.stringify(normalized));
        } catch (error) {
            console.error('Failed to persist grading policies', error);
        }
    }

    savePolicy(policy) {
        const normalizedPolicy = this.normalizePolicyShape(policy);
        const policies = this.getStoredPolicies();
        const existingIndex = policies.findIndex(p => p.id === policy.id);
        const updatedPolicies = [...policies];
        if (existingIndex >= 0) {
            updatedPolicies[existingIndex] = normalizedPolicy;
        } else {
            updatedPolicies.push(normalizedPolicy);
        }
        this.savePolicies(updatedPolicies);
        return normalizedPolicy;
    }

    // Initialize local policies for development
    initializeLocalPolicies() {
        const basePolicies = [
            {
                id: 'standard-2024',
                name: 'Standard (Current)',
                description: 'Standard grading policy for degree, diploma, certificate, and graduate certificate programs',
                associatedProgramTypes: ['Degree', 'Diploma', 'Certificate', 'Graduate Certificate'],
                gradeScale: [
                    { alphaGrade: 'A+', gradePoint: 4.0, lowPercent: 90, highPercent: 100, description: 'Excellent' },
                    { alphaGrade: 'A', gradePoint: 3.75, lowPercent: 80, highPercent: 89, description: 'Excellent' },
                    { alphaGrade: 'B+', gradePoint: 3.25, lowPercent: 75, highPercent: 79, description: 'Very good' },
                    { alphaGrade: 'B', gradePoint: 3.0, lowPercent: 70, highPercent: 74, description: 'Good' },
                    { alphaGrade: 'C+', gradePoint: 2.25, lowPercent: 65, highPercent: 69, description: 'Satisfactory' },
                    { alphaGrade: 'C', gradePoint: 2.0, lowPercent: 60, highPercent: 64, description: 'Satisfactory' },
                    { alphaGrade: 'D', gradePoint: 1.0, lowPercent: 55, highPercent: 59, description: 'Marginal' },
                    { alphaGrade: 'F', gradePoint: 0.0, lowPercent: 0, highPercent: 54, description: 'Fail' }
                ],
                sourceUrl: '',
                isHistoric: false,
                isActive: false,
                isDefault: true
            }
        ];

        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const stored = localStorage.getItem(this.storageKey);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    return parsed.reduce((acc, policy) => {
                        acc[policy.id] = policy;
                        return acc;
                    }, {});
                }
            } catch (error) {
                console.error('Failed to read policies from storage', error);
            }
        }

        return basePolicies.reduce((acc, policy) => {
            acc[policy.id] = policy;
            return acc;
        }, {});
    }

    initializeDefaults() {
        const defaults = this.getProgramPolicyDefaults();
        if (Object.keys(defaults).length === 0) {
            const defaultMap = {};
            const policies = this.getStoredPolicies();
            ProgramTypes.forEach(type => {
                const matching = policies.find(policy =>
                    (policy.associatedProgramTypes || []).includes(type) && policy.isActive !== false
                );
                if (matching) {
                    defaultMap[type] = matching.id;
                }
            });
            this.saveProgramPolicyDefaults(defaultMap);
        }

        const existing = this.getStoredPolicies();

        // Ensure bundled Conestoga defaults are always fully populated (grade scale + program types)
        const merged = DEFAULT_CONESTOGA_POLICIES.map(defaultPolicy => {
            const current = existing.find(policy => policy.id === defaultPolicy.id);

            // If the stored policy is missing key data, refresh it from the bundled defaults
            if (!current || !(current.gradeScale?.length) || !(current.associatedProgramTypes?.length)) {
                return this.normalizePolicyShape({ ...defaultPolicy, isDefault: true, isActive: true });
            }

            return this.normalizePolicyShape({
                ...defaultPolicy,
                ...current,
                gradeScale: current.gradeScale?.length ? current.gradeScale : defaultPolicy.gradeScale,
                associatedProgramTypes: current.associatedProgramTypes?.length
                    ? current.associatedProgramTypes
                    : defaultPolicy.associatedProgramTypes,
                programTypes: current.programTypes?.length ? current.programTypes : defaultPolicy.programTypes,
                isDefault: current.isDefault ?? true,
                isActive: current.isActive ?? true
            });
        });

        // Include any other custom or non-Conestoga policies that already exist
        const deprecatedIds = ['conestoga-historic-standard'];
        const extras = existing.filter(policy =>
            !DEFAULT_CONESTOGA_POLICIES.some(def => def.id === policy.id) && !deprecatedIds.includes(policy.id)
        );
        this.savePolicies([...merged, ...extras]);
    }

    // Get all grading policies
    async getPolicies(params = {}) {
        if (this.useLocalData) {
            const policies = this.getStoredPolicies();

            // Apply filters if specified
            let filteredPolicies = policies;

            if (params.programType) {
                filteredPolicies = policies.filter(policy =>
                    (policy.associatedProgramTypes || []).includes(params.programType)
                );
            }

            if (params.isActive !== undefined) {
                filteredPolicies = filteredPolicies.filter(policy =>
                    policy.isActive === params.isActive
                );
            }

            return { data: filteredPolicies, success: true };
        }

        try {
            const response = await this.client.get('/grading-policies', { params });
            return { data: response.data, success: true };
        } catch (error) {
            console.error('Error fetching policies:', error);
            return { data: [], success: false, error: error.message };
        }
    }

    // Get policy for specific program type
    async getPolicyForProgram(programType) {
        const normalizedType = this.normalizeProgramType(programType) || programType;

        if (this.useLocalData) {
            const storedDefaults = this.getProgramPolicyDefaults();
            const preferredId = storedDefaults?.[normalizedType];
            const policies = this.getStoredPolicies();

            const preferredPolicy = preferredId
                ? policies.find(p => p.id === preferredId && p.isActive)
                : null;

            const policy = preferredPolicy || policies.find(p =>
                (p.associatedProgramTypes || []).some(type => this.normalizeProgramType(type) === normalizedType) && p.isActive
            );
            return policy ? { data: policy, success: true } : { data: null, success: false };
        }

        try {
            const response = await this.client.get(`/grading-policies/by-program/${normalizedType}`);
            return { data: response.data, success: true };
        } catch (error) {
            console.error('Error fetching policy for program:', error);
            return { data: null, success: false, error: error.message };
        }
    }

    // Create a new policy
    async createPolicy(policyData) {
        if (this.useLocalData) {
            const id = `custom_${Date.now()}`;
            const newPolicy = { ...policyData, id, isDefault: false };
            this.savePolicy(newPolicy);
            return { data: newPolicy, success: true };
        }
        // This is where you would make an API call, e.g.:
        try {
            const response = await this.client.post('/grading-policies', policyData);
            return { data: response.data, success: true };
        } catch (error) {
            console.error('Error creating policy:', error);
            return { data: null, success: false, error: error.message };
        }
    }

    // Update an existing policy
    async updatePolicy(policyId, policyData) {
        if (this.useLocalData) {
            if (!this.localPolicies[policyId]) {
                return { data: null, success: false, error: 'Policy not found' };
            }
            const updatedPolicy = { ...this.localPolicies[policyId], ...policyData };
            this.savePolicy(updatedPolicy);
            return { data: updatedPolicy, success: true };
        }
        // This is where you would make an API call, e.g.:
        try {
            const response = await this.client.put(`/grading-policies/${policyId}`, policyData);
            return { data: response.data, success: true };
        } catch (error) {
            console.error('Error updating policy:', error);
            return { data: null, success: false, error: error.message };
        }
    }

    // Delete a policy
    async deletePolicy(policyId) {
        if (this.useLocalData) {
            if (!this.localPolicies[policyId]) {
                return { data: null, success: false, error: 'Policy not found' };
            }
            const remaining = this.getStoredPolicies().filter(policy => policy.id !== policyId);
            this.savePolicies(remaining);
            return { data: { message: 'Policy deleted' }, success: true };
        }
        // This is where you would make an API call, e.g.:
        try {
            const response = await this.client.delete(`/grading-policies/${policyId}`);
            return { data: response.data, success: true };
        } catch (error) {
            console.error('Error deleting policy:', error);
            return { data: null, success: false, error: error.message };
        }
    }

    // Calculate letter grade from percentage
    async calculateGrade(percentage, policyId = null, programType = 'degree', customProgramId = null) {
        const normalizedType = this.normalizeProgramType(programType) || programType;

        if (this.useLocalData) {
            return this.calculateGradeLocal(percentage, policyId, normalizedType);
        }

        try {
            const response = await this.client.post('/grading-policies/calculate-grade', {
                percentage,
                policyId,
                programType: normalizedType,
                customProgramId
            });
            return { data: response.data, success: true };
        } catch (error) {
            console.error('Error calculating grade:', error);
            // Fallback to local calculation
            return this.calculateGradeLocal(percentage, policyId, normalizedType);
        }
    }

    // Local grade calculation for development/fallback
    calculateGradeLocal(percentage, policyId = null, programType = 'degree') {
        try {
            let policy;

            const normalizedType = this.normalizeProgramType(programType) || programType;

            if (policyId) {
                policy = this.localPolicies[policyId];
            } else {
                // Find policy by program type
                policy = this.getStoredPolicies().find(p =>
                    (p.associatedProgramTypes || []).some(type => this.normalizeProgramType(type) === normalizedType) && p.isActive
                );
            }

            // If a stored policy with a valid grade scale was found, use it
            if (policy && policy.gradeScale && policy.gradeScale.length > 0) {
                const grade = policy.gradeScale.find(scale =>
                    percentage >= scale.lowPercent && percentage <= scale.highPercent
                );

                if (grade) {
                    return {
                        data: {
                            letterGrade: grade.alphaGrade,
                            letter: grade.alphaGrade,
                            gpaPoints: grade.gradePoint,
                            passingGrade: grade.gradePoint > 0,
                            percentage: percentage,
                            policy: {
                                id: policy.id,
                                name: policy.name
                            }
                        },
                        success: true
                    };
                }
            }

            // Fallback: use the canonical computeGrade() function
            const result = computeGrade({ programType: normalizedType, percent: percentage });
            return {
                data: {
                    letterGrade: result.letter,
                    letter: result.letter,
                    gpaPoints: result.gpa,
                    passingGrade: result.gpa > 0,
                    percentage: percentage,
                    policy: {
                        id: 'computeGrade-fallback',
                        name: `Conestoga ${normalizedType} Scale`
                    }
                },
                success: true
            };

        } catch (error) {
            console.error('Local grade calculation failed:', error);
            // Ultimate fallback via computeGrade
            const result = computeGrade({ programType, percent: percentage });
            return {
                data: {
                    letterGrade: result.letter,
                    letter: result.letter,
                    gpaPoints: result.gpa,
                    passingGrade: result.gpa > 0,
                    percentage: percentage,
                    error: 'Calculation used fallback'
                },
                success: true
            };
        }
    }

    // Validate policy data
    async validatePolicy(policyData) {
        const errors = [];

        // Check required fields
        if (!policyData.name) errors.push('Policy name is required');
        if (!policyData.associatedProgramTypes || policyData.associatedProgramTypes.length === 0) {
            errors.push('At least one program type is required');
        }
        if (!policyData.gradeScale || policyData.gradeScale.length === 0) {
            errors.push('Grade scale is required');
        }

        // Check grade scale integrity
        if (policyData.gradeScale) {
            const sortedGrades = [...policyData.gradeScale].sort((a, b) => b.lowPercent - a.lowPercent);

            for (let i = 0; i < sortedGrades.length - 1; i++) {
                if (sortedGrades[i].lowPercent <= sortedGrades[i + 1].highPercent) {
                    errors.push('Grade scale ranges overlap or have gaps');
                    break;
                }
            }
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    // Get supported program types
    getSupportedProgramTypes() {
        return ProgramTypes.map(type => ({ value: type, label: buildProgramLabel(type) }));
    }

    getProgramPolicyDefaults() {
        if (typeof window === 'undefined' || !window.localStorage) return {};
        try {
            const stored = localStorage.getItem(this.programDefaultsKey);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Failed to read program policy defaults', error);
            return {};
        }
    }

    saveProgramPolicyDefaults(mapping) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            localStorage.setItem(this.programDefaultsKey, JSON.stringify(mapping));
        } catch (error) {
            console.error('Failed to persist program policy defaults', error);
        }
    }

    setProgramTypeDefault(programType, policyId) {
        const defaults = this.getProgramPolicyDefaults();
        const updated = { ...defaults, [programType]: policyId };
        this.saveProgramPolicyDefaults(updated);
        this.setCurrentProgramTypeSelection(programType);
        this.setLastViewedPolicyId(policyId);
        return updated;
    }

    getDefaultPolicyForProgramType(programType) {
        const defaults = this.getProgramPolicyDefaults();
        return defaults[programType];
    }

    setCurrentProgramTypeSelection(programType) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            localStorage.setItem(this.currentProgramTypeKey, programType);
        } catch (error) {
            console.error('Failed to persist current program type', error);
        }
    }

    getCurrentProgramTypeSelection() {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        try {
            return localStorage.getItem(this.currentProgramTypeKey);
        } catch (error) {
            console.error('Failed to read current program type', error);
            return null;
        }
    }
}

// Create singleton instance
export const gradingPolicyService = new GradingPolicyService();
export default gradingPolicyService;